import { bindActionCreators } from '@reduxjs/toolkit';
import React, { lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { showMessageModal } from '../../actions/event';
import { trackConversionEvent } from '../../actions/tracking';
import { getItem, getItemAuctionUrl, getItemCommerce } from '../../selectors/item';
import { getItemRetailerContactOptions } from '../../selectors/item-retailer';
import { conversionEvents } from '../../shared/tracking';
import EmailButton from './email-button';
import FloatingAction from './floating-action';
import MessageButton from './message-button';
import PhonenumberButton from './phonenumber-button';
const EcomButton = lazy(() => import('../../components/ecom-button-v2/EcomButtonV2'));
import { RenderClientSide } from '../../components/render';
import { getRetailerId } from '../../selectors/retailer';

interface IFloatingHeader {
  visible: boolean;
}

const FloatingHeader = ({ visible }: IFloatingHeader) => {
  const { t } = useTranslation(['common', 'item']);
  const dispatch = useDispatch();
  const item = useSelector(getItem);
  const commerce = useSelector(getItemCommerce);
  const contactOptions = useSelector(getItemRetailerContactOptions);
  const auctionUrl = useSelector(getItemAuctionUrl);
  const retailerId = useSelector(getRetailerId);
  const actions = bindActionCreators(
    {
      showMessageModal,
      trackConversionEvent,
    },
    dispatch,
  );

  const shouldShowEcomButton = commerce?.enabled && !commerce.disabledOnWayke;
  const objectIsReserved = shouldShowEcomButton && commerce.reserved;

  const onAuctionClick = () =>
    actions.trackConversionEvent(item, conversionEvents.CONVERSION_ACTION_AUCTION_CLICK);

  if (!item) {
    return null;
  }

  if (
    !auctionUrl &&
    !shouldShowEcomButton &&
    !retailerId &&
    !contactOptions?.telephone?.e164 &&
    !contactOptions?.email
  )
    return null;

  return (
    <>
      <FloatingAction />
      <div data-wx-floatingheader="" className={visible ? 'is-visible' : ''}>
        <div className="floating-header-section">
          <div className="container">
            <div className="floating-header-row">
              <div className="floating-header-col">
                <div className="floating-header-heading">
                  <div className="floating-header-heading-main">{item.title}</div>
                  <div className="floating-header-heading-alt">{item.shortDescription}</div>
                </div>
              </div>
              <div className="floating-header-col">
                <ul className="floating-header-action-container">
                  {auctionUrl && (
                    <li className="floating-header-action">
                      <a
                        href={auctionUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-wx-button="action medium"
                        title={t('To auction', { ns: 'item' })}
                        onClick={onAuctionClick}
                      >
                        <div className="button-icon">
                          <i className="icon-auction no-margin" />
                        </div>
                        <div className="button-text">{t('To auction', { ns: 'item' })}</div>
                      </a>
                    </li>
                  )}

                  {!auctionUrl && (
                    <>
                      {shouldShowEcomButton && (
                        <li className="floating-header-action">
                          <RenderClientSide
                            skeleton={
                              <button
                                type="button"
                                data-wx-button="primary full-width medium"
                                title={t('Buy online', { ns: 'item' })}
                                disabled={objectIsReserved}
                              >
                                <div className="button-icon">
                                  <i className="icon-cart no-margin" />
                                </div>
                                <div className="button-text">{t('Buy online', { ns: 'item' })}</div>
                              </button>
                            }
                          >
                            <EcomButton id={item.id} isMedium disabled={objectIsReserved} />
                          </RenderClientSide>
                        </li>
                      )}
                      {retailerId && (
                        <li className="floating-header-action">
                          <MessageButton
                            onClick={actions.showMessageModal}
                            label={t('sendMessageShort', { ns: 'item' })}
                            isMedium
                          />
                        </li>
                      )}
                      {contactOptions?.telephone?.e164 && (
                        <li className="floating-header-action">
                          <PhonenumberButton
                            label={t('callShort', { ns: 'item' })}
                            isMedium
                            isLight
                            trackingContext="sticky"
                          />
                        </li>
                      )}
                      {contactOptions?.email && (
                        <li className="floating-header-action">
                          <EmailButton label={t('emailShort', { ns: 'item' })} isMedium isLight />
                        </li>
                      )}
                    </>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FloatingHeader;
